<template>
  <div class="text-start">
    <template v-if="trainingModuleContent">
      <!--trainingModuleContent title-->
      <v-row no-gutters class="mb-4">
        <v-col>
          <LocalisedContentEditable
            :values="trainingModuleContent.title"
            @update:values="updateTrainingModuleContentTitle"
            :placeholder="$t('course.courseDetails.contentNameHere')"
            :editable="isEditing"
            css-class="text-h6 font-weight-bold textLight--text"
            :hasError="trainingModuleContent.hasInvalidTitle"
            :selectedLocale="selectedLocale"
          />
        </v-col>
      </v-row>

      <!--trainingModuleContent duration-->
      <CourseNumberField
        :label="$t('course.courseDetails.contentDuration')"
        :value="trainingModuleContent.duration"
        @update:value="updateTrainingModuleContentDuration"
        :isEditing="isEditing"
        css-class="body-1 font-weight-medium textLight--text"
        class="mb-4"
      />

      <!--trainingModuleContent media-->
      <template>
        <!--trainingModuleContent media new-->
        <v-container
          v-if="(mediaUrl || savedMediaUrl) && isEditing"
          fluid
          class="pa-0 mb-4"
        >
          <AudioPlayer
            key="audioDataUrl"
            :mediaUrl="mediaUrl || savedMediaUrl"
            @onReady="
              value => {
                trainingModuleContent.duration == null
                  ? updateTrainingModuleContentDuration(value)
                  : null;
              }
            "
          />

          <v-row no-gutters justify="end" class="mt-2">
            <v-col cols="auto">
              <v-btn
                depressed
                small
                color="error"
                min-width="80"
                class="rounded-1"
                :disabled="isMediaSaving || isTrainingModuleContentSaving"
                @click="cancelMediaSelection"
              >
                {{ $t("course.courseDetails.cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>

        <!--trainingModuleContent media existing-->
        <v-container
          v-else-if="hasContents(trainingModuleContent.audios)"
          fluid
          class="pa-0 mb-4"
        >
          <AudioPlayer
            key="audioUrl"
            :mediaUrl="getAudio(trainingModuleContent.audios)"
          />

          <v-row v-if="isEditing" no-gutters justify="end" class="mt-2">
            <v-col cols="auto">
              <FileSelector accept="audio/mpeg" @onFileSelect="onFileSelect">
                <template v-slot:element="{ onClick, loading }">
                  <v-btn
                    depressed
                    small
                    color="primary"
                    min-width="80"
                    :loading="loading"
                    @click="onClick"
                    class="rounded-1"
                  >
                    <v-icon left small color="white">mdi-pencil</v-icon>

                    {{ $t("course.courseDetails.edit") }}
                  </v-btn>
                </template>
              </FileSelector>
            </v-col>
          </v-row>
        </v-container>

        <!--trainingModuleContent media empty-->
        <v-container
          v-else
          fluid
          class="fill-height justify-center pa-0 mb-4"
          style="border: 1px solid #281166; border-radius: 7px"
          :style="`height: ${trainingModuleContentMediaHeight}px`"
        >
          <FileSelector
            v-if="isEditing"
            accept="audio/mpeg"
            @onFileSelect="onFileSelect"
          >
            <template v-slot:element="{ onClick, loading }">
              <v-btn
                depressed
                outlined
                plain
                color="textLight"
                :loading="loading"
                @click="onClick"
                class="rounded-1 text-capitalize"
                style="min-width: 120px; opacity: 0.5"
              >
                <v-icon left>mdi-upload-outline</v-icon>

                {{ $t("course.courseDetails.uploadAudio") }}
              </v-btn>
            </template>
          </FileSelector>
        </v-container>
      </template>

      <!--trainingModuleContent description-->
      <v-row no-gutters class="mb-4">
        <v-col>
          <LocalisedContentEditable
            :values="trainingModuleContent.description"
            @update:values="updateTrainingModuleContentDescription"
            :text-area-rows="5"
            :placeholder="$t('course.courseDetails.contentDescriptionHere')"
            :editable="isEditing"
            css-class="body-1 font-weight-light textLight--text"
            :selectedLocale="selectedLocale"
          />
        </v-col>
      </v-row>

      <!--trainingModuleContent save button-->
      <v-row v-if="isEditing" justify="end">
        <v-col cols="auto">
          <v-btn
            small
            depressed
            color="#D75B5B"
            min-width="80"
            class="rounded-1 ms-2 white--text"
            :loading="isTrainingModuleContentDeleting"
            :disabled="isTrainingModuleContentSaving"
            @click="deleteTrainingModuleContent"
          >
            <v-icon small class="me-1">mdi-delete</v-icon>

            {{ $t("course.courseDetails.deleteModuleContent") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import LocalisedContentEditable from "@/app/core-module/components/LocalisedContentEditable";
import AudioPlayer from "../../../core-module/components/AudioPlayer";
import FileSelector from "@/app/core-module/components/FileSelector";
import CourseNumberField from "@/app/course-module/components/course-details/CourseNumberField";

export default {
  name: "CourseContentAudio",
  components: {
    CourseNumberField,
    LocalisedContentEditable,
    AudioPlayer,
    FileSelector,
  },
  props: {
    training: {
      type: Object,
      required: true,
    },
    selectedTrainingModuleContent: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    selectedLocale: {
      type: String,
      default: "en",
    },
  },
  data() {
    return {
      isTrainingModuleContentSaving: false,
      isTrainingModuleContentDeleting: false,
      isMediaSaving: false,
      mediaBlob: null,
      mediaUrl: null,
    };
  },
  computed: {
    trainingModuleContent() {
      return this.training.trainingModules[
        this.selectedTrainingModuleContent.trainingModuleIndex
      ]?.trainingModuleContents[
        this.selectedTrainingModuleContent.trainingModuleContentIndex
      ];
    },
    trainingModuleContentMediaHeight() {
      return this.$vuetify.breakpoint.xlOnly
        ? 200
        : this.$vuetify.breakpoint.lgAndUp
        ? 150
        : 125;
    },
    savedMediaUrl() {
      return this.trainingModuleContent.audioDataUrls?.find(
        x => x.lang === "en"
      )?.url;
    },
  },
  watch: {
    isEditing() {
      this.cancelMediaSelection();
    },
  },
  methods: {
    emitUpdateTraining(updateObject) {
      const clonedTraining = { ...this.training };

      if (updateObject === "DELETE") {
        clonedTraining.deletedTrainingModuleContents = [
          ...(clonedTraining.deletedTrainingModuleContents ?? []),
          clonedTraining.trainingModules[
            this.selectedTrainingModuleContent.trainingModuleIndex
          ]?.trainingModuleContents[
            this.selectedTrainingModuleContent.trainingModuleContentIndex
          ]?._id,
        ];

        clonedTraining.trainingModules[
          this.selectedTrainingModuleContent.trainingModuleIndex
        ].trainingModuleContents.splice(
          this.selectedTrainingModuleContent.trainingModuleContentIndex,
          1
        );

        this.$emit("update:training", clonedTraining);
        this.$emit("update:selectedTrainingModuleContent", null);
      } else {
        clonedTraining.trainingModules[
          this.selectedTrainingModuleContent.trainingModuleIndex
        ].trainingModuleContents[
          this.selectedTrainingModuleContent.trainingModuleContentIndex
        ] = {
          ...this.trainingModuleContent,
          ...updateObject,
        };

        this.$emit("update:training", clonedTraining);
      }
    },
    updateTrainingModuleContentTitle(values) {
      this.emitUpdateTraining({
        title: values,
        hasChangesInTitle: true,
        hasInvalidTitle: !values?.find(x => x.value?.trim()?.length),
      });
    },
    updateTrainingModuleContentDuration(value) {
      value = this.getInteger(value);

      this.emitUpdateTraining({
        duration: value,
        hasChangesInDuration: true,
      });
    },
    updateTrainingModuleContentDescription(values) {
      this.emitUpdateTraining({
        description: values,
        hasChangesInDescription: true,
      });
    },
    getUpdatedMedias() {
      if (this.mediaBlob == null) return;

      return this.supportedLanguages().reduce((result, lang) => {
        const existingMedia = this.trainingModuleContent.audios?.find(
          x => x.lang === lang
        );

        const existingLangItem = result.find(x => x.lang === lang);

        if (!existingLangItem) {
          if (lang === "en") {
            result.push({
              _id: existingMedia?._id,
              lang,
              url: this.mediaBlob,
            });
          } else if (existingMedia) {
            result.push({
              _id: existingMedia?._id,
              lang,
              url: existingMedia?.url,
            });
          }
        }

        return result;
      }, []);
    },
    onFileSelect(file) {
      this.mediaBlob = file.blob;
      this.mediaUrl = file.dataUrl;

      this.emitUpdateTraining({
        audioDataUrls: [{ lang: "en", url: this.mediaUrl }],
        trainingModuleContentAudios: this.getUpdatedMedias(),
        hasChangesInAudios: true,
      });
    },
    cancelMediaSelection() {
      this.mediaBlob = null;
      this.mediaUrl = null;

      this.emitUpdateTraining({
        audioDataUrls: null,
        trainingModuleContentAudios: null,
        hasChangesInAudios: false,
      });
    },
    deleteTrainingModuleContent() {
      if (this.isTrainingModuleContentDeleting) return;

      this.$dialog
        .confirm(
          this.$t("course.courseDetails.confirmTrainingModuleContentDelete")
        )
        .then(() => {
          this.emitUpdateTraining("DELETE");
        })
        .catch(_ => {});
    },
  },
};
</script>

<style lang="scss" scoped />
